import {
    Box,
    Flex,
    VStack,
    HStack,
    Heading,
    Text,
    Spinner,
    Image,
    SimpleGrid,
    Card,
    CardBody,
} from '@chakra-ui/react';
import YaxleLogo from '../assets/yaxleLogoBlue.png';
import { useGetStatus } from '../hooks/useGetStatus';

const getStatusColor = (pct: number): string => {
    if (pct >= 90) return 'green.500';
    if (pct >= 50) return 'yellow.500';
    return 'red.500';
};

interface StatusMetricProps {
    label: string;
    description: string;
    value: number;
    isLoading: boolean;
}

const StatusMetric = ({ label, description, value, isLoading }: StatusMetricProps) => (
    <Card borderWidth="1px" borderColor="gray.200" shadow="none" h="100%">
        <CardBody p={8}>
            <Flex direction="column" h="100%">
                <VStack align="flex-start" spacing={1}>
                    <Text fontSize="md" fontWeight="semibold" color="gray.800">
                        {label}
                    </Text>
                    <Text fontSize="sm" color="gray.500" minH="42px">
                        {description}
                    </Text>
                </VStack>
                <Box mt={6}>
                    {isLoading ? (
                        <Spinner size="lg" color="gray.400" />
                    ) : (
                        <HStack align="baseline" spacing={1} lineHeight="1">
                            <Text fontSize="5xl" fontWeight="bold" color={getStatusColor(value)} lineHeight="1">
                                {value}
                            </Text>
                            <Text fontSize="2xl" fontWeight="semibold" color={getStatusColor(value)} lineHeight="1">
                                %
                            </Text>
                        </HStack>
                    )}
                </Box>
            </Flex>
        </CardBody>
    </Card>
);

const Status = () => {
    const { data, isLoading } = useGetStatus();

    const connectionsPct = data?.connectionsPct ?? 0;
    const platformPct = data?.platformPct ?? 0;

    return (
        <Flex minH="100vh" w="100%" bg="gray.50" justify="center" py={16} px={4}>
            <VStack spacing={10} w="100%" maxW="720px" align="stretch">
                <VStack spacing={3}>
                    <Image src={YaxleLogo} alt="Yaxle" h="40px" />
                    <Heading size="lg" color="gray.800">
                        System Status
                    </Heading>
                </VStack>

                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                    <StatusMetric
                        label="Connections"
                        description={`${connectionsPct}% of connections are currently running`}
                        value={connectionsPct}
                        isLoading={isLoading}
                    />
                    <StatusMetric
                        label="Platform"
                        description="Share of core platform services healthy"
                        value={platformPct}
                        isLoading={isLoading}
                    />
                </SimpleGrid>
            </VStack>
        </Flex>
    );
};

export default Status;
